import { plural } from '@/lib/format'
import type { Labeled } from '@/lib/tone'

export type BillingReminderSettings = {
  id: string | null
  enabled: boolean
  offsets: number[]
  sendHour: number
  updatedAt: string | null
}

export type BillingReminderSettingsInput = Partial<Pick<BillingReminderSettings, 'enabled' | 'offsets' | 'sendHour'>>

export type PaymentReminderTrigger = 'auto' | 'manual'

export type PaymentReminderStatus = 'sending' | 'sent' | 'failed'

export type PaymentReminder = {
  id: string
  paymentId: string
  trigger: PaymentReminderTrigger
  offsetDays: number | null
  status: PaymentReminderStatus
  errorCode: string | null
  recipientPhone: string | null
  withBoleto: boolean
  sentAt: string | null
  createdAt: string
}

export const BILLING_OFFSETS_BEFORE = [-7, -5, -3, -2, -1]
export const BILLING_OFFSETS_AFTER = [1, 3, 5, 7, 15]
export const BILLING_DUE_DAY_OFFSET = 0
export const MAX_BILLING_OFFSETS = 8
export const BILLING_OFFSET_LIMIT = 30
export const BILLING_SEND_HOURS = Array.from({ length: 16 }, (_, index) => index + 6)
export const REMINDER_MESSAGE_MAX = 2000

export const DEFAULT_BILLING_SETTINGS: BillingReminderSettings = {
  id: null,
  enabled: false,
  offsets: [-3, 0, 1],
  sendHour: 9,
  updatedAt: null
}

export function sortOffsets(offsets: number[]): number[] {
  return [...new Set(offsets)].sort((a, b) => a - b)
}

export function toggleOffset(offsets: number[], offset: number, on: boolean): number[] {
  const next = new Set(offsets)
  if (on) next.add(offset)
  else next.delete(offset)
  return sortOffsets([...next])
}

export function billingOffsetLabel(offset: number): string {
  if (offset === 0) return 'No vencimento'
  const days = plural(Math.abs(offset), 'dia', 'dias')
  return offset < 0 ? `${days} antes` : `${days} após`
}

export function billingOffsetDescription(offset: number): string {
  if (offset === 0) return 'no dia do vencimento'
  const days = plural(Math.abs(offset), 'dia', 'dias')
  return offset < 0 ? `${days} antes do vencimento` : `${days} depois do vencimento`
}

export const sendHourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`

export function billingSettingsError(settings: Pick<BillingReminderSettings, 'enabled' | 'offsets' | 'sendHour'>): string | null {
  const offsets = sortOffsets(settings.offsets)
  if (offsets.some(offset => !Number.isInteger(offset) || Math.abs(offset) > BILLING_OFFSET_LIMIT)) {
    return `Os avisos vão de ${BILLING_OFFSET_LIMIT} dias antes a ${BILLING_OFFSET_LIMIT} dias depois do vencimento`
  }
  if (offsets.length > MAX_BILLING_OFFSETS) return `Escolha no máximo ${MAX_BILLING_OFFSETS} avisos`
  if (settings.enabled && offsets.length === 0) return 'Escolha pelo menos um aviso para ativar o envio automático'
  if (!BILLING_SEND_HOURS.includes(settings.sendHour)) return 'Escolha um horário de envio entre 06:00 e 21:00'
  return null
}

export function billingSchedulePreview(offsets: number[], sendHour: number): string[] {
  return sortOffsets(offsets).map(offset => `${billingOffsetDescription(offset)}, às ${sendHourLabel(sendHour)}`)
}

export function billingSettingsSummary(settings: Pick<BillingReminderSettings, 'enabled' | 'offsets'>): string {
  if (!settings.enabled) return 'Desligados'
  return plural(sortOffsets(settings.offsets).length, 'aviso por parcela', 'avisos por parcela')
}

export function reminderTriggerLabel(reminder: Pick<PaymentReminder, 'trigger' | 'offsetDays'>): string {
  if (reminder.trigger === 'manual') return 'Enviado pela equipe'
  return reminder.offsetDays === null ? 'Automático' : `Automático · ${billingOffsetLabel(reminder.offsetDays).toLowerCase()}`
}

const REMINDER_STATUS: Record<PaymentReminderStatus, Labeled> = {
  sending: { label: 'Enviando', tone: 'info' },
  sent: { label: 'Enviado', tone: 'success' },
  failed: { label: 'Não enviado', tone: 'danger' }
}

export function reminderStatus(status?: string | null): Labeled {
  return REMINDER_STATUS[status as PaymentReminderStatus] ?? REMINDER_STATUS.sending
}

export const BILLING_ERROR_MESSAGES: Record<string, string> = {
  PAYMENT_NOT_OPEN: 'Esta cobrança não está em aberto. Só dá para lembrar pagamentos pendentes ou vencidos.',
  PAYMENT_CANCELED: 'Esta cobrança está cancelada.',
  RENTAL_TENANT_MISSING: 'O contrato não tem locatário vinculado.',
  BILLING_RECIPIENT_PHONE_MISSING: 'O locatário não tem telefone cadastrado.',
  BILLING_WHATSAPP_MISSING: 'Nenhum WhatsApp está marcado para os avisos de cobrança.',
  BILLING_WHATSAPP_NOT_CONNECTED: 'O WhatsApp de cobrança está desconectado. Conecte-o em Integrações → WhatsApp.',
  BILLING_WHATSAPP_DISABLED: 'A integração do WhatsApp está desativada.',
  BILLING_RECIPIENT_NOT_ON_WHATSAPP: 'O telefone do locatário não tem WhatsApp.'
}

export function reminderFailureMessage(code?: string | null): string {
  return (code && BILLING_ERROR_MESSAGES[code]) || 'O WhatsApp não conseguiu entregar o aviso.'
}

export const BOLETO_ACCEPT = 'application/pdf,image/jpeg,image/png'
export const BOLETO_MAX_BYTES = 15 * 1024 * 1024

const BOLETO_TYPES = BOLETO_ACCEPT.split(',')

export function boletoFileError(file: Pick<File, 'type' | 'size'>): string | null {
  if (!BOLETO_TYPES.includes(file.type)) return 'Envie o boleto em PDF, JPG ou PNG'
  if (file.size > BOLETO_MAX_BYTES) return 'O arquivo passa de 15 MB'
  return null
}

export function boletoKindLabel(fileName?: string | null): string {
  return /\.pdf$/i.test(fileName ?? '') ? 'PDF' : 'Imagem'
}
